import { useQuery } from "@tanstack/react-query";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchSystemAnalytics } from "@/api/dashboard";
import StatsCard from "@/components/dashboard/StatsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity,
  Database,
  Server,
  BarChart2,
  CheckCircle,
  AlertTriangle,
  Clock,
  HardDrive,
  Zap,
  Shield,
  Globe,
} from "lucide-react";

const SystemAnalyticsDashboard = () => {
  // Fetch system analytics data
  const { data: systemData, isLoading } = useQuery({
    queryKey: ["systemAnalytics"],
    queryFn: fetchSystemAnalytics,
    refetchInterval: 30 * 1000, // Refresh every 30 seconds for system metrics
  });

  // Fetch client IP address
  const { data: clientIP, isLoading: ipLoading } = useQuery({
    queryKey: ["clientIP"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_client_ip");
      if (error) {
        console.error("Error getting client IP:", error);
        throw error;
      }
      return data as string;
    },
    refetchInterval: 60 * 1000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <AdminLayout title="System Analytics">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!systemData) {
    return (
      <AdminLayout title="System Analytics">
        <div className="p-8 text-center">
          <h2 className="text-xl font-medium mb-2">
            Unable to load system analytics
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="System Analytics">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">System Analytics</h1>
            <p className="text-muted-foreground">
              System performance, health monitoring, and operational metrics
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Client IP:{" "}
                {ipLoading ? "Loading..." : String(clientIP || "Unknown")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart2 className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">
                Live monitoring • Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>

        {/* System Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="System Health"
            value={systemData.systemHealth}
            icon={<CheckCircle className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  Healthy
                </Badge>
              </div>
            }
          />

          <StatsCard
            title="Database Status"
            value={systemData.databaseConnections}
            icon={<Database className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                Connection pool healthy
              </div>
            }
          />

          <StatsCard
            title="API Response Time"
            value={systemData.apiResponseTime}
            icon={<Zap className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                Average response time
              </div>
            }
          />

          <StatsCard
            title="System Uptime"
            value={systemData.uptime}
            icon={<Clock className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                Last 30 days availability
              </div>
            }
          />
        </div>

        {/* System Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center">
              <Activity className="h-4 w-4 mr-2 text-primary" />
              System Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* CPU Usage */}
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  23%
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  CPU Usage
                </div>
                <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mt-3">
                  <div
                    className="bg-blue-600 dark:bg-blue-400 rounded-full h-2 transition-all duration-300"
                    style={{ width: "23%" }}
                  />
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  Optimal range
                </div>
              </div>

              {/* Memory Usage */}
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  45%
                </div>
                <div className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Memory Usage
                </div>
                <div className="w-full bg-green-200 dark:bg-green-800 rounded-full h-2 mt-3">
                  <div
                    className="bg-green-600 dark:bg-green-400 rounded-full h-2 transition-all duration-300"
                    style={{ width: "45%" }}
                  />
                </div>
                <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                  Good utilization
                </div>
              </div>

              {/* Storage Usage */}
              <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  67%
                </div>
                <div className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                  Storage Usage
                </div>
                <div className="w-full bg-orange-200 dark:bg-orange-800 rounded-full h-2 mt-3">
                  <div
                    className="bg-orange-600 dark:bg-orange-400 rounded-full h-2 transition-all duration-300"
                    style={{ width: "67%" }}
                  />
                </div>
                <div className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                  Monitor closely
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Database & Security */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Database Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center">
                <Database className="h-4 w-4 mr-2 text-primary" />
                Database Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                      <Database className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">
                        Active Connections
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Database pool
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">12/50</div>
                    <div className="text-xs text-muted-foreground">
                      connections
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                      <HardDrive className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Query Performance</h3>
                      <p className="text-xs text-muted-foreground">
                        Average execution time
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">45ms</div>
                    <div className="text-xs text-muted-foreground">
                      avg time
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                      <Activity className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Total Tables</h3>
                      <p className="text-xs text-muted-foreground">
                        Database schema
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {systemData.totalTables}
                    </div>
                    <div className="text-xs text-muted-foreground">tables</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security & Monitoring */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center">
                <Shield className="h-4 w-4 mr-2 text-primary" />
                Security & Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Security Status</h3>
                      <p className="text-xs text-muted-foreground">
                        All systems secure
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-200"
                  >
                    Secure
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Recent Errors</h3>
                      <p className="text-xs text-muted-foreground">
                        Last 24 hours
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {systemData.recentErrors.length}
                    </div>
                    <div className="text-xs text-muted-foreground">errors</div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                      <Server className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Server Status</h3>
                      <p className="text-xs text-muted-foreground">
                        All services running
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-200"
                  >
                    Online
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                      <Globe className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Client IP Address</h3>
                      <p className="text-xs text-muted-foreground">
                        Current connection IP
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {ipLoading ? "Loading..." : String(clientIP || "Unknown")}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {clientIP &&
                      typeof clientIP === "string" &&
                      clientIP.includes(":")
                        ? "IPv6"
                        : "IPv4"}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center">
              <BarChart2 className="h-4 w-4 mr-2 text-primary" />
              System Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Load Balancing */}
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">Balanced</div>
                <div className="text-sm text-muted-foreground">
                  Load Distribution
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Even across all servers
                </div>
              </div>

              {/* Cache Hit Rate */}
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">94%</div>
                <div className="text-sm text-muted-foreground">
                  Cache Hit Rate
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Excellent performance
                </div>
              </div>

              {/* Request Rate */}
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">1.2K</div>
                <div className="text-sm text-muted-foreground">
                  Requests/Hour
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Normal traffic
                </div>
              </div>

              {/* Error Rate */}
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">0.1%</div>
                <div className="text-sm text-muted-foreground">Error Rate</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Well within limits
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  System Status: All Systems Operational
                </span>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                All services are running normally. Performance metrics are
                within expected ranges. No immediate action required.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default SystemAnalyticsDashboard;
